import { JobListItem } from './jobListItem'

export type FilterRowShape = {
  sold: boolean
  deleted: boolean
  hasDensityToggle: boolean
  hasSort?: boolean
}

export type FilterColumnWidths = {
  search: number
  soldDateOrEstimateStatus: number
  jobStatusOrEstimateType: number
  visibility: number
  sort: number
  view: number
}

export const FILTER_ROW_COLUMNS = 24

/* The same filters are on offer in both views, so the row only ever grows by the
   board's own view options — Sort, and the density toggle inside the View column.
   The status filter is never dropped: the board's columns happen to show status too,
   but taking the filter away made the two views filter by different things.

   Every combination below totals 24 or less, including ones no page currently renders —
   the row must not depend on callers avoiding a shape. */
export const filterColumnWidths = (
  { sold, deleted, hasDensityToggle, hasSort }: FilterRowShape
): FilterColumnWidths => {
  const showsEstimateStatus = !deleted && !sold
  const showsJobStatus = !deleted && sold
  const showsEstimateType = !sold
  const squeezed = !!hasSort && (showsEstimateStatus || showsJobStatus)

  return {
    search: hasSort ? (squeezed ? 4 : 6) : hasDensityToggle ? 6 : 7,
    soldDateOrEstimateStatus: sold || showsEstimateStatus ? (squeezed ? 4 : 5) : 0,
    jobStatusOrEstimateType: showsEstimateType
      ? (hasSort && !showsEstimateStatus ? 8 : 6)
      : showsJobStatus ? 4 : 0,
    visibility: !deleted && sold ? (squeezed ? 3 : 4) : 0,
    sort: hasSort ? 4 : 0,
    view: hasDensityToggle ? 5 : 4
  }
}

export const totalFilterColumns = (widths: FilterColumnWidths) =>
  Object.values(widths).reduce((total, width) => total + width, 0)

export type BoardSortColumn =
  | 'soldDate'
  | 'estimateDateUtc'
  | 'finalPrice'
  | 'name'
  | 'customer'
  | 'property'
  | 'jobNumber'
  | 'salesPerson'

export type SortOption = {
  value: BoardSortColumn
  label: string
}

export const boardSortValue: Record<BoardSortColumn, (card: JobListItem) => string | number> = {
  soldDate: card => card.soldDate ?? '',
  estimateDateUtc: card => card.estimateDateUtc ?? '',
  finalPrice: card => card.finalPrice ?? 0,
  name: card => (card.name ?? '').toLowerCase(),
  customer: card => (card.customer ?? '').toLowerCase(),
  property: card => (card.property ?? '').toLowerCase(),
  jobNumber: card => (card.jobNumber ?? '').toLowerCase(),
  salesPerson: card => (card.salesPerson ?? '').toLowerCase()
}

/* The values must match the whitelist in the API's JobSearchQueryBuilder.AddOrderBy;
   anything it does not recognise silently falls back to ordering by job id.
   Status is deliberately absent: every card in a column already shares one
   status, so sorting by it would order nothing. */
export const boardSortOptions = ({ sold, deleted }: { sold: boolean, deleted: boolean }): SortOption[] => {
  const dateOption: SortOption = sold
    ? { value: 'soldDate', label: 'Sold date' }
    : { value: 'estimateDateUtc', label: 'Estimate date' }
  const priceOption: SortOption[] = deleted ? [] : [{ value: 'finalPrice', label: 'Price' }]
  const remaining: SortOption[] = [
    { value: 'name', label: 'Name' },
    { value: 'customer', label: 'Customer' },
    { value: 'property', label: 'Property' },
    { value: 'jobNumber', label: 'Job number' },
    { value: 'salesPerson', label: 'Sales person' }
  ]

  return [dateOption, ...priceOption, ...remaining]
}

export const boardSortColumn = (sortColumn: string | undefined, options: SortOption[]): BoardSortColumn =>
  options.find(option => option.value === sortColumn)?.value ?? options[0].value
